/**
 * The success chime, ported from the prototype's `playDoneChime`.
 *
 * Synthesised rather than shipped as an audio file — it is ~40 lines against a ~20 KB
 * asset and an extra request, and the shape was tuned by ear in the prototype, so it is
 * carried over note for note. A "ta-daa": one short pickup note, then a fall onto a major
 * chord that rings out, which reads as an announcement rather than a system beep.
 *
 * Entirely best-effort. A browser may refuse to start audio, the device may be muted, and
 * an iPhone's silent switch kills it outright — none of which is a failure worth telling
 * the user about, so every path here is silent about its own problems.
 */

/** Level-matched against the other candidates tried in the prototype. */
const BUS_GAIN = 0.236;

/** G5, plus its octave, as the pickup. */
const PICKUP = [
  [783.99, 0.45],
  [1567.98, 0.14],
];

/** …resolving onto C major. */
const CHORD = [
  [1046.5, 0.5],
  [1318.51, 0.34],
  [1567.98, 0.28],
  [2093.0, 0.14],
];

let ctx = null;
let dry = null;
let verb = null;

/**
 * A small algorithmic room: a noise burst with an exponential tail, used as an impulse
 * response. No asset, a handful of lines, and it is most of what keeps the chime from
 * sounding like a bare oscillator.
 */
function buildReverb(audio) {
  const rate = audio.sampleRate;
  const length = Math.floor(rate * 1.7);
  const impulse = audio.createBuffer(2, length, rate);
  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.6);
    }
  }
  const convolver = audio.createConvolver();
  convolver.buffer = impulse;
  return convolver;
}

function setup() {
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtor) return false;

  if (!ctx) {
    ctx = new AudioCtor();
    verb = buildReverb(ctx);
    const wet = ctx.createGain();
    wet.gain.value = 0.2;
    dry = ctx.createGain();
    dry.gain.value = 1;
    verb.connect(wet);
    wet.connect(ctx.destination);
    dry.connect(ctx.destination);
  }

  // Submitting was a tap, so the gesture requirement is satisfied — but the context can
  // still come up suspended on mobile.
  if (ctx.state === "suspended") ctx.resume();
  return true;
}

/** Play the chime. Never throws, never reports; callers do not check a result. */
export function playSuccessChime() {
  try {
    if (!setup()) return;

    const bus = ctx.createGain();
    bus.gain.value = BUS_GAIN;
    bus.connect(dry);
    bus.connect(verb);

    const start = ctx.currentTime + 0.02;

    const note = (frequency, at, gain, decay) => {
      const osc = ctx.createOscillator();
      const envelope = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = frequency;
      // Exponential ramps cannot touch zero, hence the near-silent floor.
      envelope.gain.setValueAtTime(0.0001, at);
      envelope.gain.exponentialRampToValueAtTime(gain, at + 0.012);
      envelope.gain.exponentialRampToValueAtTime(0.0001, at + decay);
      osc.connect(envelope);
      envelope.connect(bus);
      osc.start(at);
      osc.stop(at + decay + 0.05);
    };

    PICKUP.forEach(([frequency, gain]) => note(frequency, start, gain, 0.26));
    CHORD.forEach(([frequency, gain]) => note(frequency, start + 0.17, gain, 1.25));
  } catch (e) {
    /* audio is decoration; never let it break the done screen */
  }
}
